import { useDispatch } from 'react-redux';
import {
  createColor,
  createColorWithOpacity,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { useModal } from '~/modal/useModal.hook';
import { BoundaryRowComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/boundaryItem/boundaryRow.component';
import type { SortedBTGs } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTools.types';
import { useBoundaryItems } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryItems';
import { useBTGFillData } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBTGFillData';
import { useGetBoundaryTerritoryDisplayNames } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryTerritoryDisplayName';
import { ActiveDataLayerComponent } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/listing/activeDataLayer.component';
import type {
  BoundaryGroupId, BoundaryTerritoryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryGroupsSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import type { BoundaryTerritory } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useDataLayersTemplates } from '~/store/frontendState/mapTools/dataLayers/useDataLayersTemplates';
import { mapSettingsDataLayersRemoveDataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import type { DataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import { setDataLayersFilter } from '~/store/mapSettings/toolsState/dataLayers/mapSettingsToolsStateDataLayers.actionCreators';
import { useDataLayersFilters } from '~/store/mapSettings/toolsState/dataLayers/mapSettingsToolsStateDataLayers.selectors';
import { BoundaryTerritoryContextMenuComponent } from '../../boundary/activeBoundary/boundaryItem/boundaryTerritoryContextMenu.component';
import { useBoundaryExportData } from '../../boundary/hooks/useBoundaryExportData';

export const ActiveDataLayerContainer = ({
  boundaryGroupId,
  boundaryTerritoryGroups,
  dataLayer,
}: Readonly<{
  boundaryGroupId: BoundaryGroupId;
  boundaryTerritoryGroups: SortedBTGs;
  dataLayer: DataLayer;
}>) => {
  const dispatch = useDispatch();
  const boundaryGroups = useBoundaryGroupsSelector();
  const { getBoundaryTerritoryDisplayName } = useGetBoundaryTerritoryDisplayNames();
  const boundaryFilters = useDataLayersFilters();
  const { exportBoundaryTerritoryData } = useBoundaryExportData();
  const { createNewTemplate, updateDataLayerTemplate, deleteDataLayerTemplate } = useDataLayersTemplates();
  const { openModal: openExportContainedBoundaries } = useModal('ExportContainedBoundaryData');
  const { openModal: openDataLayerEdit } = useModal('DataLayerEdit');
  const { openModal: openDataLayerFillEdit } = useModal('DataLayerFillEdit');
  const { openModal: openDataLayerStyleEdit } = useModal('DataLayerStyleEdit');

  const filter = boundaryFilters.get(boundaryGroupId);
  const boundaryGroupName = boundaryGroups.find(bg => bg.id === boundaryGroupId)?.name;

  const dropdownProps = useBTGFillData({
    boundaryGroupId,
    boundaryTerritoryGroups,
  });

  const boundaryTerritoryGroup = dropdownProps.activeBoundaryTerritoryGroup;

  const boundaryItems = useBoundaryItems({ boundaryTerritoryGroup });

  const handleDataLayerRemove = () => {
    dispatch(mapSettingsDataLayersRemoveDataLayer(dataLayer));
  };

  const handleDataLayerEdit = () => {
    openDataLayerEdit({ dataLayerId: dataLayer.id });
  };

  const handleDataLayerFillEdit = () => {
    openDataLayerFillEdit({ dataLayerId: dataLayer.id });
  };

  const handleDataLayerStyleEdit = () => {
    openDataLayerStyleEdit({ dataLayerId: dataLayer.id });
  };

  const handleSaveAsTemplate = () => {
    if (dataLayer.fromTemplate) {
      updateDataLayerTemplate(dataLayer.fromTemplate, dataLayer);
    }
    else {
      createNewTemplate(dataLayer);
    }
  };

  const handleDeleteTemplate = () => {
    if (dataLayer.fromTemplate) {
      deleteDataLayerTemplate(dataLayer.fromTemplate);
    }
    handleDataLayerRemove();
  };

  const onToggleShowAll = () => {
    dispatch(setDataLayersFilter(boundaryTerritoryGroup.boundaryGroupId, {
      ...filter,
      filteredBoundaryTerritories: [],
      filteredBoundaries: [],
      showAll: filter.showAll === TriStateRange.Full
        ? TriStateRange.None
        : TriStateRange.Full,
    }));
  };

  const setBoundaryTerritoriesFilter = (filteredBoundaryTerritories: BoundaryTerritoryId[], showAll: TriStateRange) => {
    dispatch(setDataLayersFilter(boundaryTerritoryGroup.boundaryGroupId, {
      ...filter,
      filteredBoundaryTerritories,
      showAll,
    }));
  };

  const getFilterBoundaryTerritoryToggleHandler = (boundaryTerritory: BoundaryTerritory) => () => {
    const newFilter = filter.filteredBoundaryTerritories
      .filter(territoryId => territoryId !== boundaryTerritory.boundaryTerritoryId);

    if (newFilter.length === filter.filteredBoundaryTerritories.length) {
      newFilter.push(boundaryTerritory.boundaryTerritoryId);
    }
    const showAll = newFilter.length ? TriStateRange.Partial : TriStateRange.None;

    setBoundaryTerritoriesFilter(newFilter, showAll);
  };

  return (
    <ActiveDataLayerComponent
      boundaryGroupName={boundaryGroupName}
      dataLayer={dataLayer}
      dropdownProps={dropdownProps}
      toolbarProps={{
        onDeleteTemplate: dataLayer.fromTemplate ? handleDeleteTemplate : undefined,
        onSaveAsTemplate: handleSaveAsTemplate,
        onRemove: handleDataLayerRemove,
        onEditLayers: handleDataLayerEdit,
        onEditFill: handleDataLayerFillEdit,
        onEditStyle: handleDataLayerStyleEdit,
      }}
      showAllCheckmark={filter.showAll}
      onToggleShowAll={onToggleShowAll}
    >
      {boundaryItems.displayBoundaryTerritories && boundaryItems.boundaryTerritoryList.map(boundaryTerritory => {
        const setting = {
          isVisible: filter.filteredBoundaryTerritories.includes(boundaryTerritory.boundaryTerritoryId),
          label: getBoundaryTerritoryDisplayName(boundaryTerritory.boundaryTerritoryId,
            boundaryTerritoryGroup.boundaryTerritoryGroupId),
          selectedColor: createColorWithOpacity(boundaryTerritory.style.color, boundaryTerritory.style.opacity / 100),
          lineColor: createColor(boundaryTerritoryGroup.settings.style.lineColor),
          lineWidth: boundaryTerritoryGroup.settings.style.lineWidth,
          onToggle: getFilterBoundaryTerritoryToggleHandler(boundaryTerritory),
          onExportData: () => exportBoundaryTerritoryData(
            boundaryTerritoryGroup.boundaryTerritoryGroupId,
            boundaryTerritory.boundaryTerritoryId,
          ),
          onExportContainedBoundaries: () => openExportContainedBoundaries({
            filter: {
              type: 'boundary_territory',
              boundaryTerritoryIds: [boundaryTerritory.boundaryTerritoryId],
              boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
            },
          }),
        };

        return (
          <BoundaryRowComponent
            key={boundaryTerritory.boundaryTerritoryId}
            isChecked={setting.isVisible}
            label={setting.label}
            onToggle={setting.onToggle}
            selectedColor={setting.selectedColor}
            lineColor={setting.lineColor}
            lineWidth={setting.lineWidth}
            actions={(
              <BoundaryTerritoryContextMenuComponent
                onExportDataClick={setting.onExportData}
                onExportContainedBoundariesClick={setting.onExportContainedBoundaries}
              />
            )}
          />
        );
      })}
    </ActiveDataLayerComponent>
  );
};
